/* eslint-disable no-unused-vars */
import React from 'react';
import PropTypes from 'prop-types';
import {gql} from 'apollo-boost';
import {Query} from 'react-apollo';
import {useStore} from '../helpers/react-hookstore';
import Text from "./Text";
import TextImage from "./TextImage";
import FeatureCards from "./FeatureCards/FeatureCards";
import FeatureCardsAlt from "./FeaturedCards2021/FeatureCardsAlt";
import HoverCards from "./HoverCards/HoverCards";
import ImageText from "./ImageText";
import Images from "./Images";
import ComponentHero from "./Hero/ComponentHero";
import InnerHeroItem from "./Hero/InnerHeroItem";
import CTA from "./CTA";
import GeneralContent from "./GeneralContent";
import CenteredCallout from './CenteredCallout/CenteredCallout';
import Accordion from "./Accordion/Accordion";
import InnerAccordionItem from "./Accordion/InnerAccordionItem";
import InnerCardHover from "./HoverCards/InnerCardHover";
import InnerCardFeatureBox from "./FeatureCards/InnerCardFeatureBox";
import InnerCardFeatureBoxAlt from "./FeaturedCards2021/InnerCardFeatureBoxAlt";
import SplitContent from "./SplitContent/SplitContent";
import InnerSplitItem from "./SplitContent/InnerSplitItem";
import QuoteCarousel from "./QuoteCarousel/QuoteCarousel";
import CustomBlock from "./CustomBlock";
import AngleDividerComponent from "./AngleDivider/AngleDividerComponent";
import AngledHeroContentBox from "./Hero/AngledHero/AngledHeroContentBox";
import AngledHero from "./Hero/AngledHero/AngledHero";
import BioCards from "./BioCards/BioCards";
import BioCard from "./BioCards/BioCard";
import OnTheMarketChartsStatic from "../pages/Charts/BASIChartsStatic";
import InlineDealers from "./InlineDealer/InlineDealers";
import SefTableComponent from "./SefTableComponent";
import KnowledgeCenterQuery from "./KnowledgeCenter/KnowledgeCenterQuery";
import Slideshow from "./Slideshow/Slideshow";
import IconicList from "./IconicList/IconicList";
import FloatingDownload from "./FloatingDownload/FloatingDownload";
import SideLabel from "./SideLabel/SideLabel";
import StatBoxes from "./StatBoxes/StatBoxes";
import ButtonCards from "./ButtonCards/ButtonCards";
import VideoCards from "./VideoCards/VideoCards";
import SplitCtaHero from "./SplitCtaHero/SplitCtaHero";
import BookviewerFeed from "./BookviewerFeed/BookviewerFeed";
import MobileComponentSection from "./YITMMobile/MobileComponentSection";
import MobileComponentVideoCarousel from "./YITMMobile/MobileComponentVideoCarousel";
import MobileComponentFeaturedArticle from "./YITMMobile/MobileComponentFeaturedArticle";
import MobileComponentDownloadCard from "./YITMMobile/MobileComponentDownloadCard";
import MobileComponentCalloutList from "./YITMMobile/MobileComponentCalloutList";
import MobileComponentNewsItemList from "./YITMMobile/MobileComponentNewsItemList";
import MobileComponentCallout from "./YITMMobile/MobileComponentCallout";
import MobileComponentAwardList from "./YITMMobile/MobileComponentAwardList";
import CareersQuery from "./Careers/CareersQuery";
import MobileComponentImage from "./YITMMobile/MobileComponentImage";
import JobSearchQuery from "./Jobs/JobSearchQuery";
import FeaturedCardsFullWidth from "./FeaturedCardsFullWidth/FeaturedCardsFullWidth";
import InnerFeaturedCardFullWidth from "./FeaturedCardsFullWidth/InnerFeaturedCardFullWidth";
import VerticalSplitContent from "./VerticalSplitContent/VerticalSplitContent";
import ContainedHero from "./ContainedHero/ContainedHero";
import CenteredPictureCarousel from "./CenteredPictureCarousel/CenteredPictureCarousel";
import QuoteCarouselAlt from "./QuoteCarouselAlt/QuoteCarouselAlt";
import InnerQuoteCarouselAlt from "./QuoteCarouselAlt/InnerQuoteCarouselAlt";

interface Data {
    paragraphById: {
        type: Array<{
            targetId: string;
        }>;
    }
}

class ParagraphQuery extends Query<Data, {id: string}> {}

const GET_PARAGRAPH = gql`
query paragraphRevisionById($id: String!) {
  paragraphById:paragraphRevisionById(id: $id) {
    id:revisionId
    ... on Paragraph {
      type {
        targetId
      }
    }
  }
}
`;

/**
 * Handles Drupal Paragraphs and routes them to the proper component
 *
 * @param id
 * @param componentIndex
 * @param index
 * @param stylesObject
 * @param numberOfComponents
 * @constructor
 */
const Paragraph = ({id, componentIndex, index, stylesObject, handleLoadedComponents}) => {

    const [ loadedComponents, setLoadedComponents ] = useStore('loadedComponentStore');

    // Function to handle marking component status
    const markComponentLoadedAs = (componentId, componentStatus) => {
        loadedComponents[componentId] = componentStatus;
        setLoadedComponents(loadedComponents);
        handleLoadedComponents(loadedComponents);
    };

    // Mark Component as Loading in Global store if it does not exist
    if (!Object.keys(loadedComponents).includes(id)) {
        markComponentLoadedAs(id, false);
    }

    return <ParagraphQuery query={GET_PARAGRAPH} variables={{ id }}>
            {({ loading, error, data }) => {

                if (loading && componentIndex === 0) {
                    return (<div className="pageLoader"/>);
                }
                else if (loading) {
                    return null;
                }
                if (error || !data.paragraphById) return <div>{error || null}</div>;

                const type = data.paragraphById.type.targetId || '';
                const paragraphID = `${data.paragraphById.id}`;
                

                // Stats Boxes
                if (type === 'component_stats_boxes') {
                    return (<StatBoxes id={paragraphID} markComponentLoadedAs={markComponentLoadedAs}/>);
                }

                // Button Cards
                if (type === 'component_button_cards') {
                    return (<ButtonCards id={paragraphID} markComponentLoadedAs={markComponentLoadedAs}/>);
                }

                // Video Cards
                if (type === 'component_video_cards') {
                    return (<VideoCards  id={paragraphID} handleLoadedComponents={handleLoadedComponents} markComponentLoadedAs={markComponentLoadedAs}/>);
                }

                // CTA
                if ((type === 'object_cta') || (type === 'object_featured_item_cta')) {
                    return (<CTA id={paragraphID} handleLoadedComponents={handleLoadedComponents} markComponentLoadedAs={markComponentLoadedAs}/>);
                }

                // Iconic List
                if (type === 'component_iconic_list') {
                    return (<IconicList id={paragraphID} handleLoadedComponents={handleLoadedComponents} markComponentLoadedAs={markComponentLoadedAs}/>);
                }

                // General Content
                if (type === 'component_general_content') {
                    return (<GeneralContent id={paragraphID} handleLoadedComponents={handleLoadedComponents} markComponentLoadedAs={markComponentLoadedAs}/>);
                }

                // Centered Callout
                if(type === 'component_centered_callout') {
                    return (<CenteredCallout id={paragraphID} handleLoadedComponents={handleLoadedComponents} markComponentLoadedAs={markComponentLoadedAs}/>);
                }

                // Slidershow
                if (type === 'component_image_slide_show') {
                    return (<Slideshow id={paragraphID} handleLoadedComponents={handleLoadedComponents} markComponentLoadedAs={markComponentLoadedAs}/>);
                }

                // Hero
                if (type === 'component_hero') {
                    return (<ComponentHero id={paragraphID} componentIndex={componentIndex} handleLoadedComponents={handleLoadedComponents} markComponentLoadedAs={markComponentLoadedAs}/>);
                }

                if (type === 'inner_hero_item') {
                    return (<InnerHeroItem id={paragraphID} componentIndex={componentIndex} index={index} handleLoadedComponents={handleLoadedComponents} markComponentLoadedAs={markComponentLoadedAs}/>);
                }

                if (type === 'component_contained_hero') {
                    return (<ContainedHero id={paragraphID} markComponentLoadedAs={markComponentLoadedAs}/>);
                }

                // Accordion
                if (type === 'component_accordion') {
                    return (<Accordion id={paragraphID} handleLoadedComponents={handleLoadedComponents} markComponentLoadedAs={markComponentLoadedAs}/>);
                }

                if (type === 'inner_accordion_item') {
                    return (<InnerAccordionItem id={paragraphID} handleLoadedComponents={handleLoadedComponents} markComponentLoadedAs={markComponentLoadedAs}/>);
                }

                // Cards

                if (type === 'component_feat_cards_container') {
                    return (<FeatureCards id={paragraphID} handleLoadedComponents={handleLoadedComponents} markComponentLoadedAs={markComponentLoadedAs}/>);
                }

                if(type === 'component_feat_card_container_2') {
                    return (<FeatureCardsAlt id={paragraphID} handleLoadedComponents={handleLoadedComponents} markComponentLoadedAs={markComponentLoadedAs}/>);
                }

                if (type === 'component_hover_cards_container') {
                    return (<HoverCards id={paragraphID} handleLoadedComponents={handleLoadedComponents} markComponentLoadedAs={markComponentLoadedAs}/>);
                }

                if (type === 'component_bio_cards') {
                    return (<BioCards id={paragraphID} handleLoadedComponents={handleLoadedComponents} markComponentLoadedAs={markComponentLoadedAs}/>);
                }

                if (type === 'inner_bio_photo') {
                    return (<BioCard id={paragraphID} handleLoadedComponents={handleLoadedComponents} markComponentLoadedAs={markComponentLoadedAs}/>)
                }

                if (type === 'inner_card_hover') {
                    return (<InnerCardHover id={paragraphID} stylesObject={stylesObject} handleLoadedComponents={handleLoadedComponents} markComponentLoadedAs={markComponentLoadedAs}/>);
                }

                if (type === 'inner_card_feature_box') {
                    return (<InnerCardFeatureBox id={paragraphID} stylesObject={stylesObject} handleLoadedComponents={handleLoadedComponents} markComponentLoadedAs={markComponentLoadedAs}/>);
                }

                if(type === 'inner_card_feature_box_2') {
                    return (<InnerCardFeatureBoxAlt id={paragraphID} stylesObject={stylesObject} handleLoadedComponents={handleLoadedComponents} markComponentLoadedAs={markComponentLoadedAs}/>);
                }

                // Split Content
                if (type === 'component_split_content') {
                    return (<SplitContent id={paragraphID} handleLoadedComponents={handleLoadedComponents} markComponentLoadedAs={markComponentLoadedAs}/>);
                }

                if (type === 'inner_split_content_item') {
                    return (<InnerSplitItem id={paragraphID} handleLoadedComponents={handleLoadedComponents} markComponentLoadedAs={markComponentLoadedAs}/>);
                }

                // Quote
                if (type === 'component_quote') {
                    return (<QuoteCarousel id={paragraphID} handleLoadedComponents={handleLoadedComponents} markComponentLoadedAs={markComponentLoadedAs}/>);
                }

                // Angled Divider
                if (type === 'component_angled_divider') {
                    return (<AngleDividerComponent id={paragraphID} handleLoadedComponents={handleLoadedComponents} markComponentLoadedAs={markComponentLoadedAs}/>);
                }

                // Angled Heros
                if (type === 'component_contentbox_angled_hero') {
                    return (<AngledHeroContentBox id={paragraphID} componentIndex={componentIndex} handleLoadedComponents={handleLoadedComponents} markComponentLoadedAs={markComponentLoadedAs}/>);
                }

                if (type === 'component_angled_hero') {
                    return (<AngledHero id={paragraphID} componentIndex={componentIndex} handleLoadedComponents={handleLoadedComponents} markComponentLoadedAs={markComponentLoadedAs}/>);
                }

                // Charts
                if (type === 'component_chart_basi') {
                    return (<OnTheMarketChartsStatic id={paragraphID} handleLoadedComponents={handleLoadedComponents} markComponentLoadedAs={markComponentLoadedAs}/>);
                }

                // SEF Data
                if (type === 'pre_defined_content_sef_table') {
                    return (<SefTableComponent id={paragraphID} handleLoadedComponents={handleLoadedComponents} markComponentLoadedAs={markComponentLoadedAs}/>);
                }

                // Knowledge Center
                if (type === 'pre_defined_knowledge_center') {
                    return (<KnowledgeCenterQuery id={paragraphID} handleLoadedComponents={handleLoadedComponents} markComponentLoadedAs={markComponentLoadedAs}/>);
                }

                // Careers
                if (type === 'pre_defined_careers') {
                    return (<CareersQuery id={paragraphID} handleLoadedComponents={handleLoadedComponents} markComponentLoadedAs={markComponentLoadedAs}/>);
                }

                // Inline Dealer
                if (type === 'component_inline_dealer') {
                    return (<InlineDealers id={paragraphID} handleLoadedComponents={handleLoadedComponents} markComponentLoadedAs={markComponentLoadedAs}/>);
                }

                if (type === 'component_side_label') {
                    return (<SideLabel id={paragraphID} handleLoadedComponents={handleLoadedComponents} markComponentLoadedAs={markComponentLoadedAs}/>);
                }

                // Floating Link Callout
                if (type === 'component_floating_link_callout') {
                    return (<FloatingDownload id={paragraphID} handleLoadedComponents={handleLoadedComponents} markComponentLoadedAs={markComponentLoadedAs}/>);
                }

                // Generic Component Types
                if (type === 'text') {
                    return (<Text id={paragraphID} handleLoadedComponents={handleLoadedComponents} markComponentLoadedAs={markComponentLoadedAs}/>);
                }

                if (type === 'text_image') {
                    return (<TextImage id={paragraphID} handleLoadedComponents={handleLoadedComponents} markComponentLoadedAs={markComponentLoadedAs}/>);
                }

                if (type === 'image_text') {
                    return (<ImageText id={paragraphID} handleLoadedComponents={handleLoadedComponents} markComponentLoadedAs={markComponentLoadedAs}/>);
                }

                if (type === 'images') {
                    return (<Images id={paragraphID} handleLoadedComponents={handleLoadedComponents} markComponentLoadedAs={markComponentLoadedAs}/>);
                }

                if (type === 'component_custom_block') {
                    return (<CustomBlock id={paragraphID} handleLoadedComponents={handleLoadedComponents} markComponentLoadedAs={markComponentLoadedAs}/>);
                }

                if (type === 'component_split_cta_hero') {
                    return (<SplitCtaHero id={paragraphID} markComponentLoadedAs={handleLoadedComponents} handleLoadedComponents={markComponentLoadedAs}/>);
                }

                if (type === 'component_bookviewer_feed') {
                    return (<BookviewerFeed/>);
                }

                if (type === 'mobile_component_section') {
                    return (<MobileComponentSection id={paragraphID} componentIndex={componentIndex} markComponentLoadedAs={handleLoadedComponents} handleLoadedComponents={markComponentLoadedAs}/>)
                }

                if (type === 'mobile_component_video_carousel') {
                    return (<MobileComponentVideoCarousel id={paragraphID} markComponentLoadedAs={handleLoadedComponents}/>)
                }

                if (type === 'mobile_component_featured_articl') {
                    return (<MobileComponentFeaturedArticle id={paragraphID} markComponentLoadedAs={handleLoadedComponents}/>)
                }

                if (type === 'mobile_component_download_cards') {
                    return (<MobileComponentDownloadCard id={paragraphID} markComponentLoadedAs={handleLoadedComponents}/>)
                }

                if (type === 'mobile_component_callout_list') {
                    return (<MobileComponentCalloutList id={paragraphID} markComponentLoadedAs={handleLoadedComponents}/>)
                }

                if (type === 'mobile_component_news_item_list') {
                    return (<MobileComponentNewsItemList id={paragraphID} markComponentLoadedAs={handleLoadedComponents}/>)
                }

                if (type === 'mobile_component_award_list') {
                    return (<MobileComponentAwardList id={paragraphID} markComponentLoadedAs={handleLoadedComponents}/>);
                }

                if (type === 'mobile_component_callout') {
                    return (<MobileComponentCallout id={paragraphID} markComponentLoadedAs={handleLoadedComponents}/>);
                }

                if (type === 'mobile_component_image') {
                    return (<MobileComponentImage id={paragraphID} markComponentLoadedAs={handleLoadedComponents}/>);
                }

                if (type === 'job_search') {
                    return (<JobSearchQuery id={paragraphID} markComponentLoadedAs={markComponentLoadedAs}/>);
                }

                if (type === 'component_featured_cards_full_wd') {
                    return (<FeaturedCardsFullWidth id={paragraphID} handleLoadedComponents={handleLoadedComponents} markComponentLoadedAs={markComponentLoadedAs}/>);
                }

                if (type === 'inner_featured_card_full_width') {
                    return (<InnerFeaturedCardFullWidth id={paragraphID} handleLoadedComponents={handleLoadedComponents} markComponentLoadedAs={markComponentLoadedAs}/>);
                }

                if (type === 'vertically_centered_split_conten') {
                    return (<VerticalSplitContent id={paragraphID} handleLoadedComponents={handleLoadedComponents} markComponentLoadedAs={markComponentLoadedAs}/>);
                }

                if (type === 'component_centered_picture_carou') {
                    return (<CenteredPictureCarousel id={paragraphID} handleLoadedComponents={handleLoadedComponents} markComponentLoadedAs={markComponentLoadedAs}/>);
                }

                if (type === 'component_quote_carousel') {
                    return (<QuoteCarouselAlt id={paragraphID} handleLoadedComponents={handleLoadedComponents} markComponentLoadedAs={markComponentLoadedAs}/>);
                }

                if (type === 'inner_quote_carousel') {
                    return (<InnerQuoteCarouselAlt id={paragraphID} handleLoadedComponents={handleLoadedComponents} markComponentLoadedAs={markComponentLoadedAs}/>);
                }

                return null;
            }}
        </ParagraphQuery>
};

Paragraph.propTypes = {
    id: PropTypes.string.isRequired,
    componentIndex: PropTypes.number,
    index: PropTypes.number,
    // eslint-disable-next-line react/forbid-prop-types
    stylesObject: PropTypes.object,
    handleLoadedComponents: PropTypes.func
};

Paragraph.defaultProps = {
    componentIndex: null,
    index: null,
    stylesObject: {},
    handleLoadedComponents: () => {}
};

export default Paragraph;



// WEBPACK FOOTER //
// ./frontend/components/Paragraph.jsx